// Hebrew letter handling for ELS search.
//
// We work with the 22 consonants of the Hebrew alphabet, indexed 0..21 in
// alphabetical order. Final-form letters (sofit) collapse to their base.
// Niqqud (vowel points) and ta'amim (cantillation marks) are stripped.

export const HEBREW_LETTERS = [
  "א", // 0  alef
  "ב", // 1  bet
  "ג", // 2  gimel
  "ד", // 3  dalet
  "ה", // 4  he
  "ו", // 5  vav
  "ז", // 6  zayin
  "ח", // 7  het
  "ט", // 8  tet
  "י", // 9  yod
  "כ", // 10 kaf
  "ל", // 11 lamed
  "מ", // 12 mem
  "נ", // 13 nun
  "ס", // 14 samekh
  "ע", // 15 ayin
  "פ", // 16 pe
  "צ", // 17 tsadi
  "ק", // 18 qof
  "ר", // 19 resh
  "ש", // 20 shin
  "ת", // 21 tav
] as const;

export const ALPHABET_SIZE = HEBREW_LETTERS.length;

export const NO_LETTER = 0xff;

// Build char -> code lookup using charCodeAt for speed.
// Hebrew block runs 0x0590..0x05FF, so a flat 256-entry table on the low byte works.
const CODE_BY_CHARCODE: Uint8Array = (() => {
  const table = new Uint8Array(0x0600).fill(NO_LETTER);
  HEBREW_LETTERS.forEach((ch, i) => {
    table[ch.charCodeAt(0)] = i;
  });
  // Final forms map to their base letter.
  const finals: Array<[string, string]> = [
    ["ך", "כ"],
    ["ם", "מ"],
    ["ן", "נ"],
    ["ף", "פ"],
    ["ץ", "צ"],
  ];
  for (const [final, base] of finals) {
    table[final.charCodeAt(0)] = table[base.charCodeAt(0)];
  }
  return table;
})();

export function letterCode(ch: string): number {
  const cc = ch.charCodeAt(0);
  if (cc >= 0x0600) return NO_LETTER;
  return CODE_BY_CHARCODE[cc];
}

export function isHebrewLetterCharCode(cc: number): boolean {
  return cc < 0x0600 && CODE_BY_CHARCODE[cc] !== NO_LETTER;
}

// Niqqud + ta'amim covers 0x0591..0x05C7. Everything in that range that isn't
// a base letter is a mark we should drop for searching purposes.
export function isMarkCharCode(cc: number): boolean {
  return cc >= 0x0591 && cc <= 0x05c7 && (cc < 0x0600 ? CODE_BY_CHARCODE[cc] === NO_LETTER : true);
}

// Strip every Hebrew mark and any non-letter character, returning a string of
// pure consonants (with finals collapsed to base via NFC-equivalent letterCode).
export function stripToConsonants(text: string): string {
  let out = "";
  for (let i = 0; i < text.length; i++) {
    const code = letterCode(text[i]!);
    if (code !== NO_LETTER) out += HEBREW_LETTERS[code];
  }
  return out;
}

// Convert a user-typed search word into letter codes. Tolerates niqqud,
// whitespace, punctuation, and final forms.
export function wordToCodes(word: string): number[] {
  const codes: number[] = [];
  for (let i = 0; i < word.length; i++) {
    const c = letterCode(word[i]!);
    if (c !== NO_LETTER) codes.push(c);
  }
  return codes;
}

export function codesToWord(codes: ArrayLike<number>): string {
  let out = "";
  for (let i = 0; i < codes.length; i++) out += HEBREW_LETTERS[codes[i]!];
  return out;
}
